import {type QueryShape, queryShape} from 'helpers/queryShape';

import {analyticsLogger} from './analyticsLogger';

export type SearchEndReason =
  | 'selected'
  | 'dismissed'
  | 'input_cleared'
  | 'route_change'
  | 'inactivity';
export type SearchOutcome =
  | 'profile_viewed'
  | 'team_viewed'
  | 'workstream_opened'
  | 'knowledge_opened'
  | 'selection_committed'
  | 'not_applicable';

export type SearchRankingVariant = 'personalized_v1';

/**
 * Bounded, server-derived facts about why a global-search result matched.
 *
 * This mirrors the additive `search_metadata` object returned by the search
 * API. It is optional because FE-1 must remain safe while BE-1 rolls out, but
 * when it is available its individual scalar fields are copied into Amplitude
 * impression and selection events. Never emit the nested object itself, raw
 * query text, names, IDs, LDAPs, emails, or relationship edges.
 */
export type SearchResultMetadata = {
  /**
   * The API's classification of the result: person, team, workstream,
   * workstream_scope, news, planning, or knowledge.
   *
   * Amplitude's `result_entity_type` comes from this field when supplied,
   * preserving the server's classification of the rendered result.
   */
  entity_type?: string;

  /**
   * How the result matched the query. Values are identifier_exact,
   * identifier_prefix, identifier_contains, display_name_exact,
   * display_name_prefix, display_name_word_start, display_name_contains,
   * title_exact, title_prefix, metadata_match, semantic_match (Knowledge
   * ranked by meaning with no title match), and unknown.
   *
   * `metadata_match` is a fallback for matches without a dedicated
   * field-and-strength bucket, including indexed auxiliary person fields and
   * non-exact, non-prefix title matches for other result types. It never
   * contains the matched text. `unknown` is a deliberate safe value when a
   * result cannot be classified.
   */
  match_bucket?: string;

  /**
   * The number of returned results with the same entity type and
   * `match_bucket`: one, two_to_five, six_plus, or not_applicable.
   *
   * This is the privacy-safe ambiguity dimension. It supports analysis of
   * common-name or broad-match searches without exposing the query or result
   * identity.
   */
  match_tier_size_bucket?: string;

  /**
   * The person's lexical relevance tier: exact_identifier,
   * exact_display_name, prefix, word_start, contains, metadata, unknown, or
   * not_applicable. It explains the lexical floor before relationship ranking
   * is applied. Non-person results are always not_applicable.
   */
  lexical_tier?: string;

  /**
   * The requester-to-person relationship used by personalized ranking:
   * manager, direct_report, peer, shared_team, none, unavailable, or
   * not_applicable.
   *
   * `unavailable` means the server could not calculate a relationship;
   * `not_applicable` is used for non-person results. This is a bounded cohort
   * label, never a raw reporting-line or team-membership edge.
   */
  relationship_bucket?: string;
};

export type SearchResultEntityType =
  | 'person'
  | 'team'
  | 'workstream'
  | 'workstream_scope'
  | 'news'
  | 'planning'
  | 'knowledge';

export type SearchAnalyticsResult = {
  entityType: SearchResultEntityType;
  localIdentity: string;
  rank: number;
  metadata?: SearchResultMetadata;
};

export type SearchEventProperties = Record<string, boolean | number | string>;

export const createSearchId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `search-${Date.now()}-${hex}`;
  }

  return `search-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const queryLengthBucket = (query: string) => {
  if (query.length === 0) return '0';
  if (query.length <= 2) return '1_2';
  if (query.length <= 8) return '3_8';
  return '9_plus';
};

const resultCountBucket = (count: number) => {
  if (count === 0) return '0';
  if (count <= 3) return '1_3';
  if (count <= 8) return '4_8';
  return '9_plus';
};

const latencyBucket = (milliseconds: number) => {
  if (milliseconds < 250) return 'under_250ms';
  if (milliseconds < 1000) return '250ms_1s';
  if (milliseconds < 3000) return '1s_3s';
  return '3s_plus';
};

const requestCountBucket = (count: number) => {
  if (count === 0) return '0';
  if (count === 1) return '1';
  if (count <= 3) return '2_3';
  return '4_plus';
};

const resultMetadataProperties = (result: SearchAnalyticsResult): SearchEventProperties => {
  const isPerson = result.entityType === 'person';
  const metadata = result.metadata;
  return {
    result_entity_type: metadata?.entity_type ?? result.entityType,
    match_bucket: metadata?.match_bucket ?? 'unknown',
    match_tier_size_bucket: metadata?.match_tier_size_bucket ?? 'unknown',
    lexical_tier: isPerson ? (metadata?.lexical_tier ?? 'unknown') : 'not_applicable',
    relationship_bucket: isPerson
      ? (metadata?.relationship_bucket ?? 'unavailable')
      : 'not_applicable',
  };
};

/** Shared mechanics for the GLOBAL_SEARCH_* event family. */
export abstract class SearchAnalyticsSession {
  protected readonly sessionId = createSearchId();
  protected requestSequence = 0;
  protected hadResults = false;
  protected hadSelection = false;
  protected ended = false;
  protected lastQueryShape: QueryShape | 'none' = 'none';
  protected rankingVariant: SearchRankingVariant;
  private readonly visibleResults = new Map<
    string,
    {requestSequence: number; initiallyVisible: boolean}
  >();

  constructor(rankingVariant: SearchRankingVariant) {
    this.rankingVariant = rankingVariant;
  }

  protected beginRequest(resultCount: number, rankingVariant: SearchRankingVariant) {
    this.requestSequence += 1;
    this.hadResults ||= resultCount > 0;
    this.rankingVariant = rankingVariant;
    return createSearchId();
  }

  protected requestQueryProperties(
    query: string,
    resultCount: number,
    shownTopK: number,
    elapsedMs: number
  ) {
    this.lastQueryShape = queryShape(query);
    return {
      query_length_bucket: queryLengthBucket(query),
      query_token_count: query.trim() ? Math.min(query.trim().split(/\s+/).length, 3) : 0,
      query_shape: this.lastQueryShape,
      has_at_symbol: query.includes('@'),
      query_edit_type: this.requestSequence === 1 ? 'initial' : 'refined',
      result_count_bucket: resultCountBucket(resultCount),
      shown_top_k: shownTopK,
      is_zero_results: resultCount === 0,
      latency_bucket: latencyBucket(elapsedMs),
    };
  }

  protected impressionProperties(result: SearchAnalyticsResult, initialResultLimit = 8) {
    if (!this.visibleResults.has(result.localIdentity)) {
      this.visibleResults.set(result.localIdentity, {
        requestSequence: this.requestSequence,
        initiallyVisible: result.rank < initialResultLimit,
      });
    }
    return {
      ...resultMetadataProperties(result),
      rank: result.rank,
      was_initially_visible: result.rank < initialResultLimit,
    };
  }

  protected selectionProperties(result: SearchAnalyticsResult) {
    this.hadSelection = true;
    const visibility = this.visibleResults.get(result.localIdentity);
    return {
      ...resultMetadataProperties(result),
      rank: result.rank,
      was_initially_visible: visibility?.initiallyVisible ?? false,
      requests_before_selection: this.requestSequence,
      selected_prior_visibility: Boolean(
        visibility && visibility.requestSequence < this.requestSequence
      ),
      selected_first_visible_request: visibility?.requestSequence ?? 0,
    };
  }

  protected terminalProperties() {
    return {
      had_results: this.hadResults,
      had_selection: this.hadSelection,
      request_count_bucket: requestCountBucket(this.requestSequence),
      last_query_shape: this.lastQueryShape,
      ranking_variant: this.rankingVariant,
    };
  }

  protected endOnce() {
    if (this.ended) return false;
    this.ended = true;
    this.visibleResults.clear();
    return true;
  }

  protected log(
    event: Parameters<ReturnType<typeof analyticsLogger>['logEvent']>[0],
    properties: SearchEventProperties
  ) {
    analyticsLogger().logEvent(event, properties);
  }
}
